import ExcelJS from "exceljs";

const resolveCellValue = (cell) => {
  if (!cell) return "";
  const val = cell.value;
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val.trim();
  if (typeof val === "number") return String(val);
  if (typeof val === "object" && val.richText) {
    return val.richText
      .map((r) => r.text)
      .join("")
      .trim();
  }
  if (typeof val === "object" && val.result !== undefined) {
    return String(val.result).trim();
  }
  return String(val).trim();
};

export const parseVendorQuotationExcel = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);
  const ws = wb.worksheets[0];
  if (!ws) throw new Error("No worksheet found in uploaded file.");

  // ── Locate the "Location" row ──────────────────────────────────────────
  let locationRowNum = null;
  ws.eachRow((row, rowNum) => {
    if (locationRowNum) return;
    const val = resolveCellValue(row.getCell(1));
    if (val.trim().toLowerCase() === "location") {
      locationRowNum = rowNum;
    }
  });

  if (!locationRowNum) {
    throw new Error(
      "Could not locate the price grid in the uploaded Excel file.",
    );
  }

  // ── Determine layout: imported vs generated ────────────────────────────
  // Imported format: row above "Location" row has brand names in col 2+
  // Generated format: "Location" row IS the brand row (merged downward)
  let brandRowNum, productRowNum;
  const rowAbove = locationRowNum > 1 ? ws.getRow(locationRowNum - 1) : null;
  const rowAboveHasBrands =
    rowAbove &&
    Array.from({ length: 10 }, (_, i) => i + 2).some(
      (c) => resolveCellValue(rowAbove.getCell(c)) !== "",
    );

  if (rowAboveHasBrands) {
    // Imported format
    brandRowNum = locationRowNum - 1;
    productRowNum = locationRowNum;
  } else {
    // Generated format
    brandRowNum = locationRowNum;
    productRowNum = locationRowNum + 1;
  }

  const brandRow = ws.getRow(brandRowNum);
  const productRow = ws.getRow(productRowNum);

  // ── Determine actual column count from the product row ─────────────────
  // More reliable than ws.columnCount for merged-cell sheets
  let totalCols = 1;
  productRow.eachCell({ includeEmpty: false }, (cell, colNum) => {
    if (colNum > totalCols) totalCols = colNum;
  });
  // Also check brand row in case product row is sparse
  brandRow.eachCell({ includeEmpty: false }, (cell, colNum) => {
    if (colNum > totalCols) totalCols = colNum;
  });

  // ── Build col → brand and col → product maps ───────────────────────────
  const colToBrand = {};
  const colToProduct = {};

  for (let c = 2; c <= totalCols; c++) {
    const brandVal = resolveCellValue(brandRow.getCell(c));
    if (brandVal) colToBrand[c] = brandVal;

    const productVal = resolveCellValue(productRow.getCell(c));
    if (productVal) colToProduct[c] = productVal;
  }

  // Fill forward brand names across merged columns
  let lastBrand = null;
  for (let c = 2; c <= totalCols; c++) {
    if (colToBrand[c]) {
      lastBrand = colToBrand[c];
    } else if (lastBrand && colToProduct[c]) {
      colToBrand[c] = lastBrand;
    }
  }

  // ── Build brands array ─────────────────────────────────────────────────
  const brandMap = new Map();
  for (let c = 2; c <= totalCols; c++) {
    const brand = colToBrand[c];
    const product = colToProduct[c];
    if (!brand || !product) continue;
    if (!brandMap.has(brand)) brandMap.set(brand, []);
    brandMap.get(brand).push(product);
  }

  const brands = Array.from(brandMap.entries()).map(([name, products]) => ({
    name,
    products: products.map((p) => ({ name: p })),
  }));

  // ── Read data rows ─────────────────────────────────────────────────────
  const locations = [];
  const entries = [];
  const dataStartRow = productRowNum + 1;

  for (let rowNum = dataStartRow; rowNum <= ws.rowCount; rowNum++) {
    const row = ws.getRow(rowNum);
    const location = resolveCellValue(row.getCell(1));
    if (!location) continue;

    // Stop at known footer markers
    if (
      location.startsWith("*") ||
      location.toLowerCase().includes("terms") ||
      location.toLowerCase().includes("dear") ||
      location.toLowerCase().includes("please")
    )
      break;

    locations.push(location);

    for (let c = 2; c <= totalCols; c++) {
      const brand = colToBrand[c];
      const product = colToProduct[c];
      if (!brand || !product) continue;
      const value = resolveCellValue(row.getCell(c));
      if (value) {
        entries.push({
          location,
          brandName: brand,
          productName: product,
          value,
        });
      }
    }
  }

  return { brands, locations, entries };
};
